import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { Download, CheckSquare, Square } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const EXPORT_ITEMS = [
  {
    id: 'deals',
    label: 'Deals',
    description: 'All deal records',
    filename: (today) => `deals_export_${today}.csv`,
    endpoint: '/api/deals/export?format=csv',
  },
  {
    id: 'transactions',
    label: 'Transactions',
    description: 'Full transaction history',
    filename: (today) => `transactions_export_${today}.csv`,
    endpoint: '/api/transactions/csv-export-all',
    responseIsArray: true,
  },
  {
    id: 'syndications',
    label: 'Syndications',
    description: 'All syndication records',
    filename: (today) => `syndications_backup_${today}.csv`,
    endpoint: '/api/syndications/csv-export',
  },
];

export default function BackupModal({ open, onClose, getAuthHeaders }) {
  const [counts, setCounts]       = useState({ deals: null, transactions: null, syndications: null });
  const [selected, setSelected]   = useState({ deals: true, transactions: true, syndications: true });
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress]   = useState(null); // null | { done, total, current }

  // Fetch live record counts when modal opens
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/api/backup/counts`, { headers: getAuthHeaders() })
      .then(r => setCounts(r.data))
      .catch(() => setCounts({ deals: '?', transactions: '?', syndications: '?' }));
  }, [open, getAuthHeaders]);

  const anySelected = Object.values(selected).some(Boolean);
  const allSelected = Object.values(selected).every(Boolean);

  const toggleAll = () => {
    const next = !allSelected;
    setSelected({ deals: next, transactions: next, syndications: next });
  };

  const fmtCount = (n) => (n == null ? '…' : Number(n).toLocaleString());

  // Download one file — returns a blob via axios
  const downloadOne = async (item) => {
    const today = new Date().toISOString().slice(0, 10);

    if (item.responseIsArray) {
      // Transactions endpoint returns a JSON array — convert to CSV using proper display headers
      const TX_COLUMNS = [
        { key: 'id',              label: 'Transaction ID' },
        { key: 'deal_display_id', label: 'Deal ID' },
        { key: 'deal_name',       label: 'Deal Name' },
        { key: 'date',            label: 'Transaction Date' },
        { key: 'amount',          label: 'Amount' },
        { key: 'type',            label: 'Transaction Type' },
        { key: 'notes',           label: 'Notes / Description' },
      ];
      const res = await axios.get(`${API}${item.endpoint}`, { headers: getAuthHeaders() });
      const rows = res.data;
      const csvCell = (v) => {
        const s = v == null ? '' : String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n')
          ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csvLines = [
        TX_COLUMNS.map(c => c.label).join(','),
        ...rows.map(r => TX_COLUMNS.map(c => csvCell(r[c.key] ?? '')).join(',')),
      ];
      const blob = new Blob([csvLines.join('\n')], { type: 'text/csv' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = item.filename(today);
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } else {
      const res = await axios.get(`${API}${item.endpoint}`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a   = document.createElement('a');
      a.href = url; a.download = item.filename(today);
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    }
  };

  const handleDownload = async () => {
    const items = EXPORT_ITEMS.filter(i => selected[i.id]);
    if (!items.length) return;

    setDownloading(true);
    setProgress({ done: 0, total: items.length, current: items[0].label });

    for (let i = 0; i < items.length; i++) {
      setProgress({ done: i, total: items.length, current: items[i].label });
      try {
        await downloadOne(items[i]);
      } catch (err) {
        toast.error(`Failed to export ${items[i].label}`);
      }
      // 500ms delay between downloads so the browser doesn't block them
      if (i < items.length - 1) await new Promise(r => setTimeout(r, 500));
    }

    setProgress({ done: items.length, total: items.length, current: null });
    toast.success(`${items.length} file${items.length > 1 ? 's' : ''} downloaded`);
    setDownloading(false);
    // Auto-close after short pause so user sees the success state
    setTimeout(() => { setProgress(null); onClose(); }, 1200);
  };

  const handleClose = () => {
    if (downloading) return;
    setProgress(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md" data-testid="backup-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Download className="h-5 w-5 text-blue-900" />
            Backup Data
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500 mt-0.5">
            Select the data you want to export. Each selection downloads as a separate CSV file.
          </DialogDescription>
        </DialogHeader>

        {/* Checkboxes */}
        <div className="space-y-3 py-2">
          {EXPORT_ITEMS.map(item => (
            <label
              key={item.id}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                ${selected[item.id] ? 'border-blue-300 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
              data-testid={`backup-checkbox-${item.id}`}
            >
              <Checkbox
                checked={selected[item.id]}
                onCheckedChange={v => setSelected(prev => ({ ...prev, [item.id]: !!v }))}
                disabled={downloading}
                className="mt-0.5"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm text-gray-900">{item.label}</span>
                  <span className="text-xs text-gray-400 font-mono">
                    {fmtCount(counts[item.id])} records
                  </span>
                </div>
                <div className="text-xs text-gray-400 mt-0.5 font-mono truncate">
                  {item.filename(new Date().toISOString().slice(0, 10))}
                </div>
              </div>
            </label>
          ))}
        </div>

        {/* Progress indicator */}
        {progress && (
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm" data-testid="backup-progress">
            {progress.done < progress.total ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-blue-700">
                  <span>Downloading {progress.current}…</span>
                  <span>{progress.done}/{progress.total}</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-1.5">
                  <div
                    className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                    style={{ width: `${(progress.done / progress.total) * 100}%` }}
                  />
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-green-700">
                <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                All {progress.total} file{progress.total > 1 ? 's' : ''} downloaded successfully
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-1">
          <button
            onClick={toggleAll}
            disabled={downloading}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 disabled:opacity-40"
            data-testid="backup-toggle-all-btn"
          >
            {allSelected
              ? <><Square className="h-3.5 w-3.5" />Deselect All</>
              : <><CheckSquare className="h-3.5 w-3.5" />Select All</>}
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={handleClose}
              disabled={downloading}
              className="px-3 py-1.5 text-sm border rounded hover:bg-gray-50 disabled:opacity-40"
              data-testid="backup-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={handleDownload}
              disabled={downloading || !anySelected}
              className="flex items-center gap-2 px-4 py-1.5 bg-blue-900 text-white text-sm rounded hover:bg-blue-800 disabled:opacity-40 font-medium"
              data-testid="backup-download-btn"
            >
              <Download className="h-3.5 w-3.5" />
              {downloading ? 'Downloading…' : `Download Selected (${Object.values(selected).filter(Boolean).length})`}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
